import store from '..';
import { vrvTypes } from '../actionTypes';
import { fetchGateways as apiFetchGateways, fetchIdus as apiFetchIdus, sendCommand as apiSendCommand, sendBulkCommand as apiBulkCommand, createSchedule as apiCreateSchedule, fetchVrvSchedules, deleteVrvSchedule as apiDeleteSchedule, toggleVrvSchedule as apiToggleSchedule, updateVrvSchedule as apiUpdateSchedule } from '../../VRV/api';
import { messages } from '../../common/notification';

// ─── Mock data (matches the design image exactly) ───────────────────────────

const GW1 = 'D83ADDD06D89';
const GW2 = 'A12BCDE34F56';

const mkIdu = (num, gwId, status, setTemp, currentTemp, fanSpeed) => {
    const id = String(num).padStart(3, '0');
    const bacnet = 1411000 + num;
    return {
        iduDeviceId: `${gwId}-${bacnet}`,
        vrvGatewayId: gwId,
        bacnetDeviceId: bacnet,
        name: `IDU-${id}`,
        latestStatus: {
            Status: status,
            Mode: 1,
            SetTemperature: setTemp,
            FanSpeed: fanSpeed,
            AirflowDir: 1,
            CurrentTemperature: currentTemp,
            LastUpdatedTime: Math.floor(Date.now() / 1000),
            updatedAt: new Date().toISOString(),
        },
    };
};

// Status: 0=OFF 1=ON | FanSpeed: 1=LOW 4=TOP
const MOCK_IDUS_GW1 = [
    mkIdu(1, GW1, 0, 22, 29.7, 1),
    mkIdu(2, GW1, 0, 21, 26.7, 4),
    mkIdu(3, GW1, 0, 25, 26.9, 4),
    mkIdu(4, GW1, 0, 19, 29.7, 4),
    mkIdu(5, GW1, 0, 25, 26.6, 4),
    mkIdu(6, GW1, 1, 19, 26.3, 4),
    mkIdu(7, GW1, 1, 19, 25.3, 4),
    mkIdu(8, GW1, 0, 20, 28.3, 4),
    mkIdu(9, GW1, 0, 19, 29.7, 4),
    mkIdu(10, GW1, 0, 19, 29.1, 4),
    mkIdu(11, GW1, 0, 23, 27.7, 4),
    mkIdu(12, GW1, 0, 22, 21.7, 4),
    mkIdu(13, GW1, 0, 19, 28.4, 4),
    mkIdu(14, GW1, 0, 20, 27.1, 4),
    mkIdu(15, GW1, 1, 19, 25.8, 4),
    mkIdu(16, GW1, 1, 19, 26.2, 4),
    mkIdu(17, GW1, 0, 19, 29.0, 4),
    mkIdu(18, GW1, 0, 22, 28.9, 4),
    mkIdu(19, GW1, 0, 19, 27.6, 4),
    mkIdu(20, GW1, 1, 22, 27.0, 4),
    mkIdu(21, GW1, 0, 19, 29.3, 4),
    mkIdu(22, GW1, 0, 23, 27.5, 4),
    mkIdu(23, GW1, 0, 22, 29.0, 4),
    mkIdu(24, GW1, 0, 23, 26.0, 4),
];

const MOCK_IDUS_GW2 = Array.from({ length: 8 }, (_, i) =>
    mkIdu(i + 1, GW2, i % 3 === 0 ? 1 : 0, 20 + i, 25 + i * 0.5, i === 0 ? 1 : 4)
);

const MOCK_GATEWAYS = [
    { vrvGatewayId: GW1, deviceId: 'GW-OFFICE-01', lastSeenAt: new Date().toISOString(), iduCount: 24 },
    { vrvGatewayId: GW2, deviceId: 'GW-OFFICE-02', lastSeenAt: new Date().toISOString(), iduCount: 8 },
];

const MOCK_IDUS = { [GW1]: MOCK_IDUS_GW1, [GW2]: MOCK_IDUS_GW2 };

// Set to false once backend is live
const USE_MOCK = false;

// ─── Action creators ─────────────────────────────────────────────────────────

const setLoadingGateways = (val) => store.dispatch({ type: vrvTypes.SET_LOADING_GATEWAYS, payload: val });
const setLoadingIdus = (val) => store.dispatch({ type: vrvTypes.SET_LOADING_IDUS, payload: val });
const setCommandLoading = (val) => store.dispatch({ type: vrvTypes.SET_COMMAND_LOADING, payload: val });
const setBulkCommandLoading = (val) => store.dispatch({ type: vrvTypes.SET_BULK_COMMAND_LOADING, payload: val });

// ─── Async actions ────────────────────────────────────────────────────────────

export const loadGateways = async () => {
    setLoadingGateways(true);
    try {
        let gateways;
        if (USE_MOCK) {
            gateways = MOCK_GATEWAYS;
        } else {
            const res = await apiFetchGateways();
            if (!res?.success) {
                messages.error(res?.message)
            }
            gateways = res?.data || (Array.isArray(res) ? res : []);
        }
        store.dispatch({ type: vrvTypes.LOAD_GATEWAYS, payload: gateways });
        const persisted = store.getState().vrv.selectedGateways || [];
        if (persisted.length > 0) {
            const validIds = persisted.filter(id => gateways.some(gw => gw.vrvGatewayId === id));
            if (validIds.length > 0) {
                store.dispatch({ type: vrvTypes.SELECT_GATEWAY, payload: validIds });
                await loadIdusForGateways(validIds, { silent: true });
            } else {
                store.dispatch({ type: vrvTypes.SELECT_GATEWAY, payload: [] });
            }
        }
    } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to load gateways';
        messages.error(msg);
    } finally {
        setLoadingGateways(false);
    }
};

export const selectGateways = async (vrvGatewayIds) => {
    store.dispatch({ type: vrvTypes.SELECT_GATEWAY, payload: vrvGatewayIds });
    if (vrvGatewayIds.length > 0) {
        await loadIdusForGateways(vrvGatewayIds);
    } else {
        store.dispatch({ type: vrvTypes.LOAD_IDUS, payload: [] });
    }
};

export const loadIdusForGateways = async (vrvGatewayIds, { silent = false } = {}) => {
    if (!silent) {
        setLoadingIdus(true);
        store.dispatch({ type: vrvTypes.LOAD_IDUS, payload: [] });
    }
    try {
        let allIdus;
        if (USE_MOCK) {
            allIdus = vrvGatewayIds.flatMap(id => MOCK_IDUS[id] || []);
        } else {
            const result = await apiFetchIdus(vrvGatewayIds.join(','));
            if (!silent) {
                if (result.success) {
                    messages.success(result.message);
                }
                else {
                    messages.error(result.message);
                }
            }
            let mapFloorName = new Map();
            const gateways = store.getState().vrv.gateways;
            gateways.forEach((gateway) => {
                mapFloorName.set(gateway.vrvGatewayId, gateway.name);
            });
            allIdus = result.data.map(item => {
                const gatewayName = mapFloorName.get(item.vrvGatewayId) || null;
                return { ...item, latestStatus: { ...item.latestStatus, floorName: `${gatewayName}` } };
            });
        }
        store.dispatch({ type: vrvTypes.LOAD_IDUS, payload: allIdus });
    } catch (e) {
        if (!silent) messages.error(e?.response?.data?.message || e?.message || 'Failed to load IDUs');
    } finally {
        if (!silent) setLoadingIdus(false);
    }
};

export const sendBulkVrvCommand = async ({ selectedIduIds, pointName, value, maxAttempts }) => {
    setBulkCommandLoading(true);
    try {
        const login = store.getState().login;
        const allIdus = store.getState().vrv.idus;
        const selectedIdus = allIdus.filter(idu => selectedIduIds.includes(idu.iduDeviceId));

        const byGateway = {};
        selectedIdus.forEach(idu => {
            if (!byGateway[idu.vrvGatewayId]) byGateway[idu.vrvGatewayId] = [];
            const cmd = { iduDeviceId: idu.iduDeviceId, pointName, value };
            if (maxAttempts !== undefined) cmd.maxAttempts = maxAttempts;
            byGateway[idu.vrvGatewayId].push(cmd);
        });

        const payload = {
            controllerType: 'savings-dashboard',
            controllerId: `${login?.profile?.first_name} (${login?.profile?.email})`,
            controllerDetails: { userId: login?.profile?._id },
            commands: Object.entries(byGateway).map(([vrvGatewayId, vrvGatewayCommands]) => ({
                vrvGatewayId,
                vrvGatewayCommands,
            })),
        };

        const res = await apiBulkCommand(payload);
        if (res?.success) {
            store.dispatch({
                type: vrvTypes.BULK_UPDATE_IDUS,
                payload: selectedIduIds.map(id => ({ iduDeviceId: id, updates: { [pointName]: value } })),
            });
            messages.success(res.message || 'Bulk command sent');
            return { success: true };
        }
        messages.error(res?.message || 'Bulk command failed');
        return { success: false };
    } catch (e) {
        messages.error(e?.response?.data?.message || 'Bulk command failed');
        return { success: false };
    } finally {
        setBulkCommandLoading(false);
    }
};

export const createVrvSchedule = async (payload) => {
    try {
        const res = await apiCreateSchedule(payload);
        if (res?.success) {
            const newSchedule = res.data?.schedule;
            if (newSchedule) {
                store.dispatch({ type: vrvTypes.ADD_SCHEDULE, payload: newSchedule });
            }
            messages.success(res.message || 'Schedule created');
            return { success: true };
        }
        messages.error(res?.message || 'Failed to create schedule');
        return { success: false };
    } catch (e) {
        messages.error(e?.response?.data?.message || 'Failed to create schedule');
        return { success: false };
    }
};

export const sendVrvCommand = async ({ iduDeviceId, pointName, value, vrvGatewayId }) => {
    setCommandLoading(true);
    try {
        if (USE_MOCK) {
            await new Promise(r => setTimeout(r, 600));
            store.dispatch({ type: vrvTypes.UPDATE_IDU, payload: { iduDeviceId, updates: { [pointName]: value } } });
            messages.success('Command sent');
            return { success: true };
        }
        const res = await apiSendCommand({ iduDeviceId, pointName, value, vrvGatewayId });
        if (res?.success) {
            store.dispatch({ type: vrvTypes.UPDATE_IDU, payload: { iduDeviceId, updates: { [pointName]: value } } });
            messages.success(res.message || 'Command sent');
            return { success: true };
        }
        messages.error(res?.message || 'Command failed');
        return { success: false };
    } catch (e) {
        messages.error(e?.response?.data?.message || 'Command failed');
        return { success: false };
    } finally {
        setCommandLoading(false);
    }
};

export const loadVrvSchedules = async (houseId) => {
    store.dispatch({ type: vrvTypes.SET_LOADING_SCHEDULES, payload: true });
    try {
        const res = await fetchVrvSchedules(houseId);
        if (res?.success) {
            store.dispatch({ type: vrvTypes.LOAD_SCHEDULES, payload: res.data?.schedules || [] });
        } else {
            messages.error(res?.message || 'Failed to load schedules');
        }
    } catch (e) {
        messages.error(e?.response?.data?.message || 'Failed to load schedules');
    } finally {
        store.dispatch({ type: vrvTypes.SET_LOADING_SCHEDULES, payload: false });
    }
};

export const deleteVrvSchedule = async (scheduleId) => {
    try {
        const res = await apiDeleteSchedule(scheduleId);
        if (res?.success) {
            store.dispatch({ type: vrvTypes.DELETE_SCHEDULE, payload: scheduleId });
            messages.success(res.message || 'Schedule deleted');
            return { success: true };
        }
        messages.error(res?.message || 'Failed to delete schedule');
        return { success: false };
    } catch (e) {
        messages.error(e?.response?.data?.message || 'Failed to delete schedule');
        return { success: false };
    }
};

export const toggleVrvSchedule = async (scheduleId, enabled) => {
    try {
        const res = await apiToggleSchedule(scheduleId, enabled);
        if (res?.success) {
            store.dispatch({ type: vrvTypes.TOGGLE_SCHEDULE, payload: { scheduleId, enabled } });
            messages.success(res.message || 'Schedule updated');
            return { success: true };
        }
        messages.error(res?.message || 'Failed to update schedule');
        return { success: false };
    } catch (e) {
        messages.error(e?.response?.data?.message || 'Failed to update schedule');
        return { success: false };
    }
};

export const updateVrvSchedule = async (payload) => {
    try {
        const res = await apiUpdateSchedule(payload);
        if (res?.success) {
            store.dispatch({ type: vrvTypes.UPDATE_SCHEDULE, payload: res.data?.schedule || { ...payload, _id: payload.scheduleId } });
            messages.success(res.message || 'Schedule updated');
            return { success: true };
        }
        messages.error(res?.message || 'Failed to update schedule');
        return { success: false };
    } catch (e) {
        messages.error(e?.response?.data?.message || 'Failed to update schedule');
        return { success: false };
    }
};
